import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { withALErrorBoundary } from "../helpers/ErrorBoundary/ALErrorBoundary";
import { useAnnouncementBarPopup } from "../context/AnnouncementBarProvider";
import useSEOSentenceToLinkConversion from "../hooks/useSEOSentenceToLinkConversion";
import { Link } from "./al_components/ALLink";

function RichTextContentful({
  input,
  references = null,
  prefixClassName = null,
  customClassName = null,
  showSEOLinks = false,
}) {
  const _input = JSON.parse(input);
  const { setPopupOpen } = useAnnouncementBarPopup();

  const assetMap = new Map();
  _input?.content.map((d) => {
    if (d?.data?.target?.sys?.linkType === "Asset") {
      const _id = d?.data?.target?.sys?.id;
      const image = references?.filter((p) => p?.contentful_id === _id)[0]?.file;
      assetMap.set(_id, image);
    }
  });

  const options = {
    renderMark: {
      [MARKS.BOLD]: (text) => <span className="bold">{text}</span>,
      [MARKS.ITALIC]: (text) => <span className="italic">{text}</span>,
      [MARKS.UNDERLINE]: (text) => <span className="underline">{text}</span>,
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => {
        if (children.length === 1 && children[0] === "") {
          return <br />;
        }
        const content = showSEOLinks ? useSEOSentenceToLinkConversion(children) : children;
        return (
          <p
            className={`al_p ${
              prefixClassName ? `${prefixClassName}-al_p ${customClassName || ""}` : ""
            }`}
            data-type="paragraph"
          >
            {content}
          </p>
        );
      },
      [BLOCKS.HEADING_1]: (node, children) => (
        <h1
          className={`al_h1 ${prefixClassName ? `${prefixClassName}-al_h1` : ""}`}
          data-type="heading_1"
        >
          {children}
        </h1>
      ),
      [BLOCKS.HEADING_2]: (node, children) => (
        <h2
          className={`al_h2 ${prefixClassName ? `${prefixClassName}-al_h2` : ""}`}
          data-type="heading_2"
        >
          {children}
        </h2>
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <h3 className={`al_h3 ${prefixClassName ? `${prefixClassName}-al_h3` : ""}`}>{children}</h3>
      ),
      [BLOCKS.HEADING_4]: (node, children) => (
        <h4 className={`al_h4 ${prefixClassName ? `${prefixClassName}-al_h4` : ""}`}>{children}</h4>
      ),
      [BLOCKS.HEADING_5]: (node, children) => (
        <h5 className={`al_h4 ${prefixClassName ? `${prefixClassName}-al_h4` : ""}`}>{children}</h5>
      ),
      [BLOCKS.HEADING_6]: (node, children) => (
        <h6 className={`al_h4 ${prefixClassName ? `${prefixClassName}-al_h4` : ""}`}>{children}</h6>
      ),
      [BLOCKS.UL_LIST]: (node, children) => (
        <ul className={`${prefixClassName ? `${prefixClassName}-ul` : ""}`}>{children}</ul>
      ),
      [BLOCKS.LIST_ITEM]: (node, children) => (
        <li className={`${prefixClassName ? `${prefixClassName}-li` : ""}`}>{children}</li>
      ),
      [BLOCKS.OL_LIST]: (node, children) => (
        <ol className={`${prefixClassName ? `${prefixClassName}-ol` : ""}`}>{children}</ol>
      ),
      [INLINES.ENTRY_HYPERLINK]: (node, children) => (
        <Link
          className={`al_link ${
            prefixClassName ? `${prefixClassName}-a ${customClassName || ""}` : ""
          }`}
          to={node.data.target}
        >
          {children}
        </Link>
      ),
      [INLINES.HYPERLINK]: (node, children) => {
        if (node.data.uri === "?offer_details=true") {
          return (
            <span
              className="underline"
              style={{ cursor: "pointer" }}
              onClick={() => setPopupOpen(true)}
            >
              {children}
            </span>
          );
        }
        return (
          <a
            className={`al_link ${
              prefixClassName ? `${prefixClassName}-a ${customClassName || ""}` : ""
            }`}
            href={node.data.uri}
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        );
      },
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const asset = assetMap.get(node.data.target.sys.id);
        return asset ? (
          <img
            className={`${prefixClassName ? `${prefixClassName}-img` : ""}`}
            style={{ maxWidth: "360px", margin: "0px auto" }}
            src={`https://${asset.url}`}
            alt=""
          />
        ) : null;
      },
      [BLOCKS.TABLE]: (node, children) => (
        <table className={`${prefixClassName ? `${prefixClassName}-table` : ""}`}>
          <tbody className={`${prefixClassName ? `${prefixClassName}-tbody` : ""}`}>
            {children}
          </tbody>
        </table>
      ),
      [BLOCKS.TABLE_ROW]: (node, children) => (
        <tr className={`${prefixClassName ? `${prefixClassName}-tr` : ""}`}>{children}</tr>
      ),
      [BLOCKS.TABLE_CELL]: (node, children) => (
        <td className={`${prefixClassName ? `${prefixClassName}-td` : ""}`}>{children}</td>
      ),
    },
  };

  return <>{documentToReactComponents(JSON.parse(input), options)}</>;
}

export default withALErrorBoundary({
  name: "RichTextContentful",
  priority: "P3",
})(RichTextContentful);
